import {
  coverageFieldKeys,
  FIELD_LENGTH_MAX,
  FIELD_LENGTH_MIN,
  isBlockField,
  textAlign,
  type FieldConfig,
  type FormConfig,
  type TextAlign,
} from '@cod/config-schema';
import { Box, Card, IconButton, Text, Title } from '@nimbus-ds/components';
import { DragDotsIcon, EyeIcon, EyeOffIcon, TrashIcon } from '@nimbus-ds/icons';
import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useFieldProblem } from './ProblemsContext.js';
import { fieldProblemPath } from './validation.js';
import {
  CheckboxControl,
  NumberControl,
  SelectControl,
  TextControl,
} from '../components/controls.js';
import { BlockControls } from './BlockControls.js';
import { CustomFieldControls } from './CustomFieldControls.js';
import { fieldCardTitle } from './fieldTypeNames.js';

type FieldPatch = Partial<FieldConfig>;
type Update = (updater: (prev: FormConfig) => FormConfig) => void;

/** Input field controls (label, placeholder, validation). Shown for non-block field types. */
function InputFieldControls({
  field,
  onPatch,
}: {
  field: FieldConfig;
  onPatch: (patch: FieldPatch) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const problem = useFieldProblem();
  // Keyed by the field's KEY, not its index — the merchant reorders these cards freely.
  const fieldError = (prop: string): string | undefined =>
    problem(fieldProblemPath(field.key, prop));
  return (
    <Box marginTop="4" display="flex" flexDirection="column" gap="4">
      <TextControl
        id={`field-label-${field.key}`}
        label={t('designer.fields.label')}
        value={field.label}
        error={fieldError('label')}
        onChange={(label) => onPatch({ label })}
      />
      <TextControl
        id={`field-placeholder-${field.key}`}
        label={t('designer.fields.placeholder')}
        value={field.placeholder}
        error={fieldError('placeholder')}
        onChange={(placeholder) => onPatch({ placeholder })}
      />
      <CheckboxControl
        name={`field-required-${field.key}`}
        label={t('designer.fields.required')}
        checked={field.required}
        onChange={(required) => onPatch({ required })}
      />
      <CheckboxControl
        name={`field-show-icon-${field.key}`}
        label={t('designer.fields.showIcon')}
        checked={field.show_icon}
        onChange={(show_icon) => onPatch({ show_icon })}
      />
      {/* 1–250 on both. 250 is the longest value any of these inputs has a reason to hold, and
          the old 1000 let a merchant cap a first name well past that. See `min_length` in
          `config-schema` for why 0 is still a legal stored value but not an offered one. */}
      <Box display="flex" gap="4" flexWrap="wrap">
        <NumberControl
          id={`field-min-${field.key}`}
          label={t('designer.fields.minLength')}
          value={field.min_length}
          error={fieldError('min_length')}
          onChange={(min_length) => onPatch({ min_length })}
          min={FIELD_LENGTH_MIN}
          max={FIELD_LENGTH_MAX}
        />
        <NumberControl
          id={`field-max-${field.key}`}
          label={t('designer.fields.maxLength')}
          value={field.max_length}
          error={fieldError('max_length')}
          onChange={(max_length) => onPatch({ max_length })}
          min={FIELD_LENGTH_MIN}
          max={FIELD_LENGTH_MAX}
        />
      </Box>
      <Box display="flex" flexDirection="column" gap="1">
        <TextControl
          id={`field-error-${field.key}`}
          label={t('designer.fields.errorText')}
          value={field.error_text}
          error={fieldError('error_text')}
          onChange={(error_text) => onPatch({ error_text })}
        />
        <Text fontSize="caption" color="neutral-textLow">
          <Trans i18nKey="designer.fields.errorHelp" components={{ b: <strong /> }} />
        </Text>
      </Box>
    </Box>
  );
}

/** One reorderable, expandable field editor card. Block fields edit the shared config via
 * `update`; input fields edit their own entry via `onPatch`. */
function FieldEditorCard({
  field,
  config,
  update,
  dragging,
  defaultOpen,
  onPatch,
  onRemove,
  onHeaderPointerDown,
}: {
  field: FieldConfig;
  config: FormConfig;
  update: Update;
  dragging: boolean;
  defaultOpen: boolean;
  onPatch: (patch: FieldPatch) => void;
  onRemove: () => void;
  /** Press anywhere on the header: `onTap` fires if released without dragging (toggle open). */
  onHeaderPointerDown: (e: ReactPointerEvent, onTap: () => void) => void;
}): JSX.Element {
  const { t } = useTranslation();
  // A just-added field mounts expanded so the merchant can edit it right away.
  const [open, setOpen] = useState(defaultOpen);
  const isCustom = field.custom === true;
  const isBlock = isBlockField(field.type);
  /**
   * A field the configured coverage turns into a cascading select.
   *
   * 🚨 Not hideable, for the same reason the submit button is not: the form does not work without
   * it. Hiding `province` leaves `city`'s dropdown permanently empty, and — worse, because it is
   * silent — `isWithinCoverage` short-circuits on the first empty value, so the merchant's whole
   * "only accept orders I can deliver" setting stops doing anything. `normalizeFormConfig` forces
   * these back on when a config already carries them hidden; this is what stops the state being
   * reachable from the Designer in the first place, so the merchant never watches a toggle undo
   * itself on the next load.
   */
  const coverageLocked = coverageFieldKeys(config.coverage).includes(
    field.key as ReturnType<typeof coverageFieldKeys>[number],
  );
  // The submit button is always rendered (a form must be submittable), so it has no hide toggle.
  const canHide = field.type !== 'submit' && !coverageLocked;
  const title = fieldCardTitle(field, t);

  return (
    <Card>
      {/* The whole header is both draggable and click-to-toggle: pressing it starts a
          potential drag, and releasing without moving toggles the card. The icon buttons
          below are excluded (see beginDrag's button guard) so they keep their own clicks. */}
      <div
        onPointerDown={(e) => onHeaderPointerDown(e, () => setOpen((o) => !o))}
        aria-label={t('designer.fields.drag')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: dragging ? 'grabbing' : 'grab',
          // Stop the browser from scrolling / selecting while a drag is in progress.
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        <span style={{ display: 'flex', color: '#9aa0a6', flex: '0 0 auto' }}>
          <DragDotsIcon />
        </span>
        <div style={{ flex: 1 }}>
          <Title as="h6">{title}</Title>
        </div>
        {canHide && (
          <IconButton
            source={field.enabled ? <EyeIcon /> : <EyeOffIcon />}
            onClick={() => onPatch({ enabled: !field.enabled })}
            aria-label={field.enabled ? t('designer.fields.hide') : t('designer.fields.show')}
          />
        )}
        {isCustom && (
          <IconButton source={<TrashIcon />} onClick={onRemove} aria-label={t('common.delete')} />
        )}
      </div>

      {open &&
        (isCustom ? (
          <Box marginTop="4">
            <CustomFieldControls field={field} onPatch={onPatch} />
          </Box>
        ) : isBlock ? (
          <Box marginTop="4">
            <BlockControls type={field.type} config={config} update={update} />
          </Box>
        ) : (
          <Box marginTop="4" display="flex" flexDirection="column" gap="2">
            {/* Says WHY the eye is missing. Without it the merchant just finds a field they
                cannot hide and no explanation anywhere on the page. */}
            {coverageLocked && (
              <Text fontSize="caption" color="neutral-textLow">
                {t('designer.fields.coverageLocked')}
              </Text>
            )}
            <InputFieldControls field={field} onPatch={onPatch} />
          </Box>
        ))}
    </Card>
  );
}

/**
 * The header editor card. The header is fixed form chrome — it always renders at the top of the
 * storefront form and never reorders — so it sits in its own card above the reorderable list,
 * carrying just a visibility toggle (the eye) and its title/icon options.
 */
export function HeaderCard({
  config,
  update,
}: {
  config: FormConfig;
  update: Update;
}): JSX.Element {
  const { t } = useTranslation();
  const problem = useFieldProblem();
  const [open, setOpen] = useState(false);
  const setHeader = (patch: Partial<FormConfig['header']>): void =>
    update((c) => ({ ...c, header: { ...c.header, ...patch } }));
  return (
    <Card>
      {/* Click the row to expand/collapse; the icon buttons keep their own clicks. No drag
          handle — the header is fixed chrome, not a reorderable field. */}
      <div
        onClick={(e) => {
          if (!(e.target as HTMLElement).closest('button')) setOpen((o) => !o);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <div style={{ flex: 1 }}>
          <Title as="h6">{t('designer.fields.headerCard')}</Title>
        </div>
        <IconButton
          source={config.header.enabled ? <EyeIcon /> : <EyeOffIcon />}
          onClick={() => setHeader({ enabled: !config.header.enabled })}
          aria-label={config.header.enabled ? t('designer.fields.hide') : t('designer.fields.show')}
        />
      </div>
      {open && (
        <Box marginTop="4" display="flex" flexDirection="column" gap="4">
          <TextControl
            id="header-title"
            label={t('designer.text.headerTitle')}
            value={config.header.title}
            error={problem('header.title')}
            onChange={(title) => setHeader({ title })}
          />
          {/* Reuses the custom text block's alignment copy — the same three words for the same
              three choices, so the designer never names them two different ways. */}
          <SelectControl<TextAlign>
            id="header-align"
            label={t('designer.custom.align')}
            value={config.header.align}
            options={textAlign.options.map((a) => ({
              value: a,
              label: t(`designer.custom.align_${a}`),
            }))}
            onChange={(align) => setHeader({ align })}
          />
          <CheckboxControl
            name="header-icon"
            label={t('designer.text.headerIcon')}
            checked={config.header.show_icon}
            onChange={(show_icon) => setHeader({ show_icon })}
          />
        </Box>
      )}
    </Card>
  );
}

/**
 * Sortable list of field editors. Reordering rewrites the `fields` array order, which IS
 * the on-screen order in the renderer.
 *
 * Custom pointer-based drag (no extra deps): pressing the drag handle lifts that card so it
 * follows the pointer on the Y axis, while the cards it passes slide out of the way to open a
 * gap. The array itself is only rewritten on release, so the layout never reflows mid-drag and
 * the geometry captured at drag start stays valid throughout. The drag handle is the only
 * drag surface, so editing inputs never starts a drag.
 */
export function FieldList({
  fields,
  config,
  update,
  openKey,
  onMove,
  onPatch,
  onRemove,
}: {
  fields: FieldConfig[];
  config: FormConfig;
  update: Update;
  /** Key of a just-added field that should mount expanded. */
  openKey?: string | null;
  onMove: (from: number, to: number) => void;
  onPatch: (index: number, patch: FieldPatch) => void;
  onRemove: (index: number) => void;
}): JSX.Element {
  // Live DOM nodes of each card wrapper, measured once at drag start.
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  // Geometry frozen at drag start. Because we don't rewrite the array until release, these
  // slot centers stay valid for the whole gesture, so hit-testing never fights a reflow.
  const geom = useRef<{ centers: number[]; startY: number } | null>(null);

  // `index` is the card being dragged; `target` is the slot it would drop into; `dy` is how far
  // it has been lifted from its origin; `slot` is the vertical space it occupies (its own height
  // + the inter-card gap), i.e. how far the cards it passes must slide to open a gap for it.
  const [drag, setDrag] = useState<{
    index: number;
    target: number;
    dy: number;
    slot: number;
  } | null>(null);

  const beginDrag = (e: ReactPointerEvent, index: number, onTap: () => void): void => {
    if (e.button !== 0) return; // left button / primary touch only
    // The whole header is the drag surface, but the row's icon buttons (show/hide, delete,
    // close) are inside it — a press on one of those is its own click, not a drag or a toggle.
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    // Measure exactly the current cards (bound to `fields`, ignoring any stale trailing refs).
    const rects = fields.map((_, idx) => cardRefs.current[idx]!.getBoundingClientRect());
    const centers = rects.map((r) => r.top + r.height / 2);
    // Real inter-card gap (Box gap="2" → 8px), measured so the make-room shift is exact.
    const gap = rects.length > 1 ? Math.max(0, rects[1]!.top - rects[0]!.bottom) : 8;
    const slot = rects[index]!.height + gap;
    geom.current = { centers, startY: e.clientY };
    const startX = e.clientX;
    // A press only becomes a drag past a small threshold; released within it, it's a tap that
    // toggles the card. So the lift (setDrag) and the selection lock are deferred until then —
    // a plain click never lifts the card or disables text selection.
    const THRESHOLD = 5;
    let moved = false;
    // Tracks the live drop target across the gesture so pointerup can commit without reading state.
    let lastTarget = index;

    const onPointerMove = (ev: PointerEvent): void => {
      const g = geom.current;
      if (!g) return;
      const dy = ev.clientY - g.startY;
      if (!moved) {
        if (Math.abs(dy) < THRESHOLD && Math.abs(ev.clientX - startX) < THRESHOLD) return;
        moved = true;
        document.body.style.userSelect = 'none';
        setDrag({ index, target: index, dy, slot });
      }
      const center = g.centers[index]! + dy; // where the dragged card's middle now sits
      // Walk outward from the origin, taking a slot once the dragged card's center passes the
      // *boundary between two cards* (the midpoint of their centers) — a half-card of travel, so
      // swapping with the very next field just works. Comparing against a neighbor's full center
      // instead would require dragging a whole card-height past it before anything moved.
      // Centers are monotonic, so the midpoints are too and we can stop at the first miss.
      let target = index;
      for (let i = index + 1; i < g.centers.length; i++) {
        if (center > (g.centers[i - 1]! + g.centers[i]!) / 2) target = i;
        else break;
      }
      for (let i = index - 1; i >= 0; i--) {
        if (center < (g.centers[i]! + g.centers[i + 1]!) / 2) target = i;
        else break;
      }
      lastTarget = target;
      setDrag((d) => (d ? { ...d, dy, target } : d));
    };
    const onPointerUp = (): void => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      document.body.style.userSelect = '';
      geom.current = null;
      if (!moved) {
        // Released within the threshold: a tap — toggle the card open/closed, leave order alone.
        onTap();
        setDrag(null);
        return;
      }
      // Commit the reorder here in the event handler — NOT inside a setState updater. An updater
      // must be pure, and StrictMode double-invokes it, which fired onMove() twice (scrambling the
      // order) and warned "cannot update AppStateProvider while rendering FieldList". We read the
      // final slot from the closure (`index`/`lastTarget`), which tracks the live target without
      // touching React state. onMove + setDrag(null) batch into one render, so the passed-over
      // cards (already slid into place) stay put and only the lifted card settles home.
      if (index !== lastTarget) onMove(index, lastTarget);
      setDrag(null);
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  // How far card `i` is shifted from its resting slot for the current drag frame.
  const offsetFor = (i: number): number => {
    if (!drag) return 0;
    if (i === drag.index) return drag.dy; // the lifted card follows the pointer
    // Cards between the origin and the target slide by one slot to open the gap.
    if (drag.target > drag.index && i > drag.index && i <= drag.target) return -drag.slot;
    if (drag.target < drag.index && i >= drag.target && i < drag.index) return drag.slot;
    return 0;
  };

  return (
    <Box display="flex" flexDirection="column" gap="2">
      {fields.map((field, i) => {
        const lifted = drag?.index === i;
        return (
          <div
            key={field.key}
            ref={(el) => {
              cardRefs.current[i] = el;
            }}
            style={{
              transform: `translateY(${offsetFor(i)}px)`,
              // The lifted card tracks the pointer with no easing; the cards making room ease
              // smoothly. When no drag is active, transitions are off so the commit frame lands
              // instantly (no post-drop FLIP glitch).
              transition: drag && !lifted ? 'transform .16s ease' : 'none',
              position: 'relative',
              zIndex: lifted ? 2 : 1,
              boxShadow: lifted ? '0 8px 24px rgba(0,0,0,.18)' : 'none',
              borderRadius: 8,
              // The lifted card is a passenger of the pointer; don't let it swallow events.
              pointerEvents: lifted ? 'none' : 'auto',
            }}
          >
            <FieldEditorCard
              field={field}
              config={config}
              update={update}
              dragging={lifted}
              defaultOpen={field.key === openKey}
              onPatch={(patch) => onPatch(i, patch)}
              onRemove={() => onRemove(i)}
              onHeaderPointerDown={(e, onTap) => beginDrag(e, i, onTap)}
            />
          </div>
        );
      })}
    </Box>
  );
}
